import {
  type GetTokenSilentlyOptions,
  useAuth0,
  type User,
} from '@auth0/auth0-react'
import { useCallback } from 'react'

import { userStorage } from '~/shared/storage'
import { navigateSuite } from '~/shell/app/router'
import { LAUNCH_ERROR_PATH } from '~/shell/pages/launch/paths'

import { emrSession } from './emrSession'

/** The session, as everything outside `auth/` needs it. See {@link useRegardAuth}. */
export interface RegardAuth {
  /** Whether the user menu should offer a logout action. */
  canLogout: boolean
  /** Whether a session exists, in either mode. */
  hasSession: boolean
  /** Whether this browser is in **EMR-embedded mode** (Atlas ADR-0027). */
  isEmrEmbedded: boolean
  /** Whether Auth0 is still restoring a standalone session, during which `hasSession` is not yet decided. */
  isLoading: boolean
  /** Whether the session can only be recovered by a new launch from the EHR. */
  needsRelaunch: boolean
  /** The Auth0 user profile from the ID token; undefined in EMR-embedded mode, which has no Auth0 session. */
  user: User | undefined
  /** Reads the session's access token, from whichever mode established it. Resolves undefined where the
   * session holds none to send. */
  getSessionAccessToken: (
    options?: GetTokenSilentlyOptions,
  ) => Promise<string | undefined>
  /** Recovers a session that can no longer produce a token, however this mode recovers one. */
  reauth: () => void
  /** Ends the session and returns the browser to the suite's origin. */
  logout: () => void
}

/**
 * Hook wrapping both authentication methods, EMR-embedded and standalone Auth0.
 *
 *  The returned flags describe the session as of this render.
 */
export const useRegardAuth = (): RegardAuth => {
  const {
    getAccessTokenSilently,
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    logout: logoutOfAuth0,
    user,
  } = useAuth0()

  const isEmrEmbedded = emrSession.isEmrEmbedded()
  const hasEmrSession = emrSession.getAccessToken() !== null

  const getSessionAccessToken = useCallback(
    async (options?: GetTokenSilentlyOptions): Promise<string | undefined> => {
      if (emrSession.isEmrEmbedded()) {
        return emrSession.getAccessToken() ?? undefined
      }

      return getAccessTokenSilently(options)
    },
    [getAccessTokenSilently],
  )

  const reauth = useCallback(() => {
    if (emrSession.isEmrEmbedded()) {
      navigateSuite(LAUNCH_ERROR_PATH, { replace: true })
      return
    }

    void loginWithRedirect({
      appState: {
        returnTo:
          window.location.pathname +
          window.location.search +
          window.location.hash,
      },
      authorizationParams: {
        redirect_uri: window.location.origin,
      },
    })
  }, [loginWithRedirect])

  const logout = useCallback(() => {
    userStorage.clear()
    logoutOfAuth0({ logoutParams: { returnTo: window.location.origin } })
  }, [logoutOfAuth0])

  return {
    // Logout is meaningful only in standalone Aut0 mode.
    canLogout: !isEmrEmbedded,
    getSessionAccessToken,
    hasSession: hasEmrSession || isAuthenticated,
    isEmrEmbedded,
    isLoading,
    logout,
    // True once an EMR-embedded session's token expires.
    needsRelaunch: isEmrEmbedded && !hasEmrSession,
    reauth,
    user: isEmrEmbedded ? undefined : user,
  }
}
